import { useEffect, useState } from "react";
import { Building2, HelpCircle, Loader2, LogIn, Mail, ShieldCheck } from "lucide-react";
import { api, ApiError } from "../lib/api";

export interface UsuarioLogado {
  id: number;
  nome: string;
  email: string;
  is_dono: boolean;
  ativo: boolean;
  vinculos: { id: number; condominio_id: number; condominio_nome: string; papel: string }[];
}

interface RespLogin {
  token: string;
  usuario: UsuarioLogado;
}

/**
 * Tela de login (multiusuário). No primeiro uso do sistema — quando ainda não
 * existe nenhum usuário — ela vira "Criar o primeiro acesso (Dono)".
 */
export function LoginPage({ onEntrar }: { onEntrar: (u: UsuarioLogado, token: string) => void }) {
  const [modoSetup, setModoSetup] = useState<boolean | null>(null); // null = verificando
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  // Fluxo "Esqueci minha senha" por e-mail.
  const [rec, setRec] = useState<"off" | "email" | "codigo" | "pronto">("off");
  const [recEmail, setRecEmail] = useState("");
  const [recCodigo, setRecCodigo] = useState("");
  const [recNovaSenha, setRecNovaSenha] = useState("");
  const [recErro, setRecErro] = useState<string | null>(null);
  const [recBusy, setRecBusy] = useState(false);
  const [recSemEmail, setRecSemEmail] = useState(false); // envio por e-mail indisponível

  async function recPedirCodigo(e: React.FormEvent) {
    e.preventDefault();
    setRecErro(null);
    setRecBusy(true);
    try {
      const r = await api.post<{ ok: boolean; motivo?: string }>("/usuarios/esqueci-senha", {
        email: recEmail,
      });
      if (r.ok === false && r.motivo === "email_indisponivel") {
        setRecSemEmail(true);
      } else {
        setRec("codigo");
      }
    } catch {
      setRecErro("Não foi possível enviar agora. Tente de novo em instantes.");
    } finally {
      setRecBusy(false);
    }
  }

  async function recRedefinir(e: React.FormEvent) {
    e.preventDefault();
    setRecErro(null);
    setRecBusy(true);
    try {
      await api.post("/usuarios/redefinir-com-codigo", {
        email: recEmail,
        codigo: recCodigo,
        nova_senha: recNovaSenha,
      });
      setRec("pronto");
    } catch (err) {
      setRecErro(err instanceof ApiError ? err.message : "Código inválido ou expirado.");
    } finally {
      setRecBusy(false);
    }
  }

  function recFechar() {
    setRec("off");
    setRecEmail(""); setRecCodigo(""); setRecNovaSenha("");
    setRecErro(null); setRecSemEmail(false);
  }

  useEffect(() => {
    api
      .get<{ precisa_setup: boolean }>("/usuarios/precisa-setup")
      .then((r) => setModoSetup(r.precisa_setup))
      .catch(() => setModoSetup(false));
  }, []);

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      const url = modoSetup ? "/usuarios/setup" : "/usuarios/login";
      const body = modoSetup ? { nome, email, senha } : { email, senha };
      const r = await api.post<RespLogin>(url, body);
      onEntrar(r.usuario, r.token);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : "Não foi possível entrar. Tente de novo.");
    } finally {
      setEnviando(false);
    }
  }

  const FUNDO_NEON =
    "radial-gradient(1000px 700px at 50% -12%, rgba(34,201,184,0.16), transparent 60%)," +
    "radial-gradient(720px 620px at 12% 120%, rgba(60,110,190,0.32), transparent 60%)," +
    "linear-gradient(160deg,#0a1322 0%,#0d1a2e 55%,#0f2140 100%)";

  if (modoSetup === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-300 text-sm" style={{ background: FUNDO_NEON }}>
        <Loader2 className="animate-spin mr-2" size={16} /> Carregando…
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4" style={{ background: FUNDO_NEON }}>
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-slate-200 p-7">
        <div className="flex flex-col items-center text-center mb-5">
          <div
            className="relative w-14 h-14 rounded-2xl flex items-center justify-center mb-3"
            style={{
              background:
                "radial-gradient(130% 90% at 50% -18%, rgba(34,201,184,0.9), transparent 55%)," +
                "linear-gradient(160deg,#17263f,#0f1a2e)",
              boxShadow: "0 10px 24px rgba(8,15,30,0.4), inset 0 1px 0 rgba(255,255,255,0.12)",
            }}
          >
            <span aria-hidden className="absolute inset-x-0 top-0 h-[2px] rounded-t-2xl"
              style={{ background: "#22C9B8", boxShadow: "0 0 8px 1px rgba(34,201,184,0.9)" }} />
            <Building2 size={26} style={{ color: "#22C9B8" }} />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Síndico<span className="text-teal-600">App</span></h1>
          <p className="text-sm text-slate-500 mt-1">
            {modoSetup ? "Crie o primeiro acesso (administrador)" : "Entre com seu e-mail e senha"}
          </p>
        </div>

        <form onSubmit={enviar} className="space-y-3">
          {modoSetup && (
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Seu nome</span>
              <input
                type="text" required value={nome} onChange={(e) => setNome(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 outline-none"
                placeholder="Ex.: Airton Leonardelli"
              />
            </label>
          )}
          <label className="block">
            <span className="text-xs font-medium text-slate-600">E-mail</span>
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 outline-none"
              placeholder="voce@exemplo.com"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Senha</span>
            <input
              type="password" required value={senha} onChange={(e) => setSenha(e.target.value)}
              autoComplete={modoSetup ? "new-password" : "current-password"}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 outline-none"
              placeholder="••••••••"
            />
          </label>

          {erro && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
              {erro}
            </div>
          )}

          <button
            type="submit" disabled={enviando}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-brand-700 text-white py-2.5 text-sm font-semibold hover:bg-brand-800 disabled:opacity-60"
          >
            {enviando ? <Loader2 className="animate-spin" size={16} /> : modoSetup ? <ShieldCheck size={16} /> : <LogIn size={16} />}
            {modoSetup ? "Criar acesso e entrar" : "Entrar"}
          </button>
        </form>

        {!modoSetup && rec === "off" && (
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => { setRec("email"); setRecEmail(email); }}
              className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-brand-700"
            >
              <HelpCircle size={13} /> Esqueci minha senha
            </button>
          </div>
        )}

        {/* Passo 1: pedir o código por e-mail */}
        {!modoSetup && rec === "email" && (
          <div className="mt-4 border-t border-slate-100 pt-4">
            <p className="text-[12px] text-slate-600 mb-2 leading-snug">
              Digite o seu <b>e-mail de acesso</b>. Enviaremos um <b>código</b> para você
              criar uma nova senha.
            </p>
            <form onSubmit={recPedirCodigo} className="space-y-2">
              <input
                type="email" required value={recEmail} onChange={(e) => setRecEmail(e.target.value)}
                placeholder="seu e-mail"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500"
              />
              {recErro && <p className="text-xs text-rose-700">{recErro}</p>}
              {recSemEmail ? (
                <div className="text-left text-[12px] text-slate-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 leading-snug">
                  O envio por e-mail ainda <b>não está ativado</b> neste app. Para redefinir:
                  <br />• <b>Usuário comum:</b> peça ao <b>administrador (Dono)</b> — ele redefine na tela <b>Usuários</b>.
                  <br />• <b>Dono:</b> feche o app e rode <b>Reset-Senha-Dono.bat</b>.
                </div>
              ) : (
                <div className="flex gap-2">
                  <button type="button" onClick={recFechar}
                    className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900">Voltar</button>
                  <button type="submit" disabled={recBusy || !recEmail}
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-brand-700 text-white py-2 text-sm font-semibold hover:bg-brand-800 disabled:opacity-60">
                    {recBusy ? <Loader2 className="animate-spin" size={15} /> : <Mail size={15} />}
                    Enviar código
                  </button>
                </div>
              )}
              {recSemEmail && (
                <button type="button" onClick={recFechar}
                  className="w-full px-3 py-2 text-sm text-slate-600 hover:text-slate-900">Voltar ao login</button>
              )}
            </form>
          </div>
        )}

        {/* Passo 2: digitar o código + a nova senha */}
        {!modoSetup && rec === "codigo" && (
          <div className="mt-4 border-t border-slate-100 pt-4">
            <p className="text-[12px] text-slate-600 mb-2 leading-snug">
              Enviamos um <b>código de 6 dígitos</b> para <b>{recEmail}</b> (válido por 15
              minutos). Digite-o abaixo com a sua <b>nova senha</b>. Não recebeu? Verifique
              o spam ou volte e tente de novo.
            </p>
            <form onSubmit={recRedefinir} className="space-y-2">
              <input
                inputMode="numeric" required value={recCodigo}
                onChange={(e) => setRecCodigo(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="Código (6 dígitos)"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm tracking-widest text-center outline-none focus:border-brand-500"
              />
              <input
                type="password" required value={recNovaSenha} onChange={(e) => setRecNovaSenha(e.target.value)}
                autoComplete="new-password" placeholder="Nova senha"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500"
              />
              {recErro && <p className="text-xs text-rose-700">{recErro}</p>}
              <div className="flex gap-2">
                <button type="button" onClick={recFechar}
                  className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900">Cancelar</button>
                <button type="submit" disabled={recBusy || recCodigo.length < 6 || !recNovaSenha}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-brand-700 text-white py-2 text-sm font-semibold hover:bg-brand-800 disabled:opacity-60">
                  {recBusy ? <Loader2 className="animate-spin" size={15} /> : <ShieldCheck size={15} />}
                  Redefinir senha
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Passo 3: pronto */}
        {!modoSetup && rec === "pronto" && (
          <div className="mt-4 border-t border-slate-100 pt-4 text-center">
            <p className="text-sm text-emerald-700 mb-2">
              ✓ Senha alterada! Agora entre com o seu e-mail e a nova senha.
            </p>
            <button type="button" onClick={recFechar}
              className="text-sm text-brand-700 hover:underline">Voltar ao login</button>
          </div>
        )}

        {modoSetup && (
          <p className="text-[11px] text-slate-400 text-center mt-4 leading-snug">
            Este primeiro usuário será o <b>administrador (dono)</b>, com acesso total.
            Depois você cria os demais usuários e define as permissões.
          </p>
        )}
      </div>
    </div>
  );
}
